import { useState } from 'react';
import { formatMoney, toMinorUnits, type JobSummary } from '@teentack/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorNote, InfoNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';
import { useMyBids, useSubmitBid, useWithdrawBid } from './use-bids';

/**
 * Placing a bid on an open-budget job. [Job Poster step 9, from the teen's side]
 *
 * A bid is an application with a price on it, so it sits behind exactly the
 * same guardian consent — naming your rate to an employer is still engaging
 * with them.
 */
export function BidPanel({ job, consentApproved }: { job: JobSummary; consentApproved: boolean }) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Scoped to this job, so the panel knows whether a bid is already standing.
  const mine = useMyBids({ jobId: job.id, pageSize: 1 });
  const submit = useSubmitBid();
  const withdraw = useWithdrawBid();

  const existing = mine.data?.items[0];

  async function place(): Promise<void> {
    setError(null);
    setNotice(null);

    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter what you would charge, as a number.');
      return;
    }

    try {
      await submit.mutateAsync({
        jobId: job.id,
        // Converted from the string the person typed, so 19.99 cannot drift.
        bidAmountMinor: toMinorUnits(amount.trim(), job.pay.currency),
        currency: job.pay.currency,
      });
      setAmount('');
      setNotice('Bid placed. The employer will let you know.');
    } catch (caught) {
      setError(presentApiError(caught, 'Could not place that bid').message);
    }
  }

  if (existing) {
    const tone =
      existing.status === 'ACCEPTED' ? 'safe' : existing.status === 'REJECTED' ? 'neutral' : 'brand';

    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>
            {existing.status.charAt(0) + existing.status.slice(1).toLowerCase()}
          </Badge>
          <span className="text-sm font-bold text-ink">
            You bid {formatMoney(existing.bidAmount.amountMinor, existing.bidAmount.currency)}
          </span>
        </div>

        {existing.status === 'SUBMITTED' && (
          <Button
            variant="ghost"
            block
            disabled={withdraw.isPending}
            onClick={() => withdraw.mutate(existing.id)}
          >
            {withdraw.isPending ? 'Withdrawing…' : 'Withdraw my bid'}
          </Button>
        )}

        {existing.status === 'ACCEPTED' && (
          <InfoNote>Your bid was accepted — the job is in My work.</InfoNote>
        )}
      </div>
    );
  }

  if (!consentApproved) {
    return (
      <p className="text-sm leading-relaxed text-slate-600">
        Your guardian has to approve this job before you can bid on it.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed text-slate-600">
        This employer is open to offers. Say what you would charge for the whole job.
      </p>

      {error && <ErrorNote message={error} />}
      {notice && <InfoNote>{notice}</InfoNote>}

      <Input
        label={`Your price (${job.pay.currency})`}
        value={amount}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAmount(event.target.value)}
        inputMode="decimal"
        placeholder="45.00"
      />

      <Button block disabled={submit.isPending || amount.trim().length === 0} onClick={() => void place()}>
        {submit.isPending ? 'Placing…' : 'Place a bid'}
      </Button>
    </div>
  );
}
